// src/pages/Stock/UnitConversionsPage.jsx
// =============================================================================
// Stock / Units of Measure / Unit conversions
// =============================================================================
//
// Unit conversions are small but high-impact master data. They let the stock
// receipt flow accept user-friendly package entries such as 10 × 5 l and post
// the correct stock quantity in ml.
//
// Access is route-protected for superuser. Backend routes enforce the same rule.

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createUnitConversion,
  listUnitConversions,
  setUnitConversionActive,
  updateUnitConversion,
} from "../../services/unitConversionService";
import { listUnitsOfMeasure } from "../../services/stockMasterDataService";

import "./StockMasterDataPage.css";

const EMPTY_FORM = {
  from_unit_code: "",
  to_unit_code: "",
  factor: "",
  is_active: true,
};

function normalizeForm(row = {}) {
  return {
    from_unit_code: row.from_unit_code || "",
    to_unit_code: row.to_unit_code || "",
    factor: row.factor ?? "",
    is_active: row.is_active !== false,
  };
}

function cleanCode(value) {
  return String(value || "").trim().toLowerCase();
}

function formatUnitOption(unit) {
  if (!unit) return "";
  const code = unit.code || "";
  const symbol = unit.symbol ? ` · ${unit.symbol}` : "";
  const name = unit.name_public ? ` · ${unit.name_public}` : "";
  return `${code}${symbol}${name}`;
}

function formatFactor(value) {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 9 }) : String(value);
}

export default function UnitConversionsPage() {
  const { t } = useTranslation(["stock", "common"]);

  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const isEditing = Boolean(editingId);

  const unitsByCode = useMemo(
    () => new Map(units.map((unit) => [cleanCode(unit.code), unit])),
    [units]
  );

  const fromUnit = unitsByCode.get(cleanCode(form.from_unit_code));
  const toUnit = unitsByCode.get(cleanCode(form.to_unit_code));

  const selectedUnitsSame = cleanCode(form.from_unit_code) && cleanCode(form.from_unit_code) === cleanCode(form.to_unit_code);
  const dimensionsMismatch = Boolean(
    fromUnit &&
    toUnit &&
    String(fromUnit.dimension || "").toUpperCase() !== String(toUnit.dimension || "").toUpperCase()
  );

  const selectedDimension = fromUnit?.dimension || toUnit?.dimension || "";

  async function loadData() {
    setLoading(true);
    setError(null);
    try {
      const [unitRows, conversionRows] = await Promise.all([
        listUnitsOfMeasure({ active: "" }),
        listUnitConversions({ active: includeInactive ? "" : "true" }),
      ]);
      setUnits(unitRows);
      setRows(conversionRows);
    } catch (err) {
      setError(err?.message || t("stock:unitConversions.messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();

  }, [includeInactive]);

  const activeUnits = useMemo(
    () => units.filter((unit) => unit.is_active !== false),
    [units]
  );

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.from_unit_code,
        row.to_unit_code,
        row.from_unit_name,
        row.to_unit_name,
        row.dimension,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function resetForm() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setMessage(null);
    setError(null);
  }

  function editRow(row) {
    setForm(normalizeForm(row));
    setEditingId(row.id);
    setMessage(null);
    setError(null);
  }

  async function submitForm(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      if (selectedUnitsSame) {
        throw new Error(t("stock:unitConversions.messages.identityNotAllowed"));
      }
      if (dimensionsMismatch) {
        throw new Error(t("stock:unitConversions.messages.dimensionMismatch"));
      }

      if (isEditing) {
        await updateUnitConversion(editingId, {
          factor: form.factor,
          is_active: form.is_active,
        });
        setMessage(t("stock:unitConversions.messages.updated"));
      } else {
        await createUnitConversion(form);
        setMessage(t("stock:unitConversions.messages.created"));
      }
      resetForm();
      await loadData();
    } catch (err) {
      setError(err?.message || t("stock:unitConversions.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(row) {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await setUnitConversionActive(row.id, !row.is_active);
      setMessage(t("stock:unitConversions.messages.statusUpdated"));
      await loadData();
    } catch (err) {
      setError(err?.message || t("stock:unitConversions.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="stock-md-page">
      <header className="stock-md-header">
        <div>
          <p className="stock-md-eyebrow">{t("stock:unitConversions.eyebrow")}</p>
          <h1 className="stock-md-title">{t("stock:unitConversions.title")}</h1>
          <p className="stock-md-subtitle">{t("stock:unitConversions.subtitle")}</p>
        </div>
        <button type="button" className="stock-md-button" onClick={loadData} disabled={loading}>
          {t("common:refresh", { defaultValue: "Refresh" })}
        </button>
      </header>

      <section className="stock-md-info-box">
        {t("stock:unitConversions.guidance")}
      </section>

      {error && <div className="stock-md-error">{error}</div>}
      {message && <div className="stock-md-success">{message}</div>}

      <div className="stock-md-grid">
        <section className="stock-md-card">
          <h2 className="stock-md-card-title">
            {isEditing ? t("stock:unitConversions.form.editTitle") : t("stock:unitConversions.form.createTitle")}
          </h2>
          <p className="stock-md-card-description">{t("stock:unitConversions.form.description")}</p>

          <form onSubmit={submitForm}>
            <div className="stock-md-form-grid">
              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:unitConversions.fields.fromUnit")}</span>
                <select
                  className="stock-md-select"
                  value={form.from_unit_code}
                  onChange={(e) => updateField("from_unit_code", e.target.value)}
                  disabled={isEditing || saving}
                  required
                >
                  <option value="">{t("stock:unitConversions.placeholders.selectUnit")}</option>
                  {activeUnits.map((unit) => (
                    <option key={unit.code} value={unit.code}>{formatUnitOption(unit)}</option>
                  ))}
                </select>
                {fromUnit && <span className="stock-md-hint">{t("stock:unitConversions.hints.dimension", { dimension: fromUnit.dimension })}</span>}
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:unitConversions.fields.toUnit")}</span>
                <select
                  className="stock-md-select"
                  value={form.to_unit_code}
                  onChange={(e) => updateField("to_unit_code", e.target.value)}
                  disabled={isEditing || saving}
                  required
                >
                  <option value="">{t("stock:unitConversions.placeholders.selectUnit")}</option>
                  {activeUnits.map((unit) => (
                    <option key={unit.code} value={unit.code}>{formatUnitOption(unit)}</option>
                  ))}
                </select>
                {toUnit && <span className="stock-md-hint">{t("stock:unitConversions.hints.dimension", { dimension: toUnit.dimension })}</span>}
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:unitConversions.fields.factor")}</span>
                <input
                  className="stock-md-input"
                  type="number"
                  min="0"
                  step="0.000000001"
                  value={form.factor}
                  onChange={(e) => updateField("factor", e.target.value)}
                  disabled={saving}
                  required
                />
                <span className="stock-md-hint">{t("stock:unitConversions.hints.factor")}</span>
              </label>

              <label className="stock-md-form-field">
                <span className="stock-md-label">{t("stock:unitConversions.fields.dimension")}</span>
                <input
                  className="stock-md-input"
                  value={selectedDimension || "—"}
                  disabled
                  readOnly
                />
                <span className="stock-md-hint">{t("stock:unitConversions.hints.dimensionReadonly")}</span>
              </label>

              <label className="stock-md-checkbox-row">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => updateField("is_active", e.target.checked)}
                  disabled={saving}
                />
                {t("stock:unitConversions.fields.active")}
              </label>
            </div>

            {selectedUnitsSame && (
              <div className="stock-md-error" style={{ marginTop: 12 }}>
                {t("stock:unitConversions.messages.identityNotAllowed")}
              </div>
            )}

            {dimensionsMismatch && (
              <div className="stock-md-error" style={{ marginTop: 12 }}>
                {t("stock:unitConversions.messages.dimensionMismatch")}
              </div>
            )}

            <div className="stock-md-actions">
              <button type="button" className="stock-md-button" onClick={resetForm} disabled={saving}>
                {t("common:cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                className="stock-md-button stock-md-button--primary"
                disabled={saving || selectedUnitsSame || dimensionsMismatch}
              >
                {isEditing ? t("stock:unitConversions.actions.saveChanges") : t("stock:unitConversions.actions.create")}
              </button>
            </div>
          </form>
        </section>

        <section className="stock-md-card">
          <div className="stock-md-toolbar">
            <div>
              <h2 className="stock-md-card-title">{t("stock:unitConversions.listTitle")}</h2>
              <p className="stock-md-card-description">{t("stock:unitConversions.listDescription")}</p>
            </div>
            <label className="stock-md-checkbox-row">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
              {t("stock:unitConversions.actions.showInactive")}
            </label>
          </div>

          <input
            className="stock-md-input stock-md-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("stock:unitConversions.searchPlaceholder")}
          />

          <div className="stock-md-table-wrap" style={{ marginTop: 12 }}>
            <table className="stock-md-table">
              <thead>
                <tr>
                  <th>{t("stock:unitConversions.columns.direction")}</th>
                  <th>{t("stock:unitConversions.columns.factor")}</th>
                  <th>{t("stock:unitConversions.columns.dimension")}</th>
                  <th>{t("stock:unitConversions.columns.status")}</th>
                  <th>{t("stock:unitConversions.columns.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="stock-md-row-title">
                        {row.from_unit_code} → {row.to_unit_code}
                      </div>
                      <div className="stock-md-row-subtitle">
                        {[row.from_unit_name, row.to_unit_name].filter(Boolean).join(" → ") || "—"}
                      </div>
                    </td>
                    <td>
                      <div className="stock-md-row-title">× {formatFactor(row.factor)}</div>
                      <div className="stock-md-row-subtitle">
                        1 {row.from_unit_symbol || row.from_unit_code} = {formatFactor(row.factor)} {row.to_unit_symbol || row.to_unit_code}
                      </div>
                    </td>
                    <td>{row.dimension}</td>
                    <td>
                      <span className={`stock-md-badge ${row.is_active ? "" : "stock-md-badge--inactive"}`}>
                        {row.is_active ? t("stock:unitConversions.status.active") : t("stock:unitConversions.status.inactive")}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="stock-md-button" onClick={() => editRow(row)}>
                        {t("common:edit", { defaultValue: "Edit" })}
                      </button>{" "}
                      <button
                        type="button"
                        className="stock-md-button stock-md-button--dangerish"
                        onClick={() => toggleActive(row)}
                        disabled={saving}
                      >
                        {row.is_active ? t("stock:unitConversions.actions.deactivate") : t("stock:unitConversions.actions.activate")}
                      </button>
                    </td>
                  </tr>
                ))}
                {!visibleRows.length && (
                  <tr>
                    <td colSpan="5">
                      {loading ? t("common:loading", { defaultValue: "Loading…" }) : t("stock:unitConversions.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
